import "./styles/index.css";
import { registerSW } from "virtual:pwa-register";
import { showUpdateToast } from "@ui/update-toast";
import { createGameBus } from "@core/event-bus";
import { createGameState } from "@core/store";
import { GameLoop } from "@core/game-loop";
import { SceneManager } from "@core/scene-manager";
import { bindRenderTargets, DomRenderer } from "@rendering/dom-renderer";
import { MotionAnimationController } from "@rendering/motion-animation-controller";
import { createPlatform } from "@platform/index";
import { GameScene } from "@ui/game-scene";
import { SplashScene } from "@ui/splash";
import { MenuScene } from "@ui/menu";
import { BloomScene } from "@ui/bloom-scene";
import { SettingsScene } from "@ui/settings-scene";
import { CreditsScene } from "@ui/credits-scene";
import { OnboardingOverlay } from "@ui/onboarding";
import type { SceneId } from "@core/types";

const bootstrap = (): void => {
  const targets = bindRenderTargets(document);
  const bus = createGameBus();
  const state = createGameState();
  const platform = createPlatform();
  const renderer = new DomRenderer(targets);
  const animation = new MotionAnimationController();

  const sceneElements: Record<SceneId, HTMLElement | null> = {
    splash: document.getElementById("scene-splash"),
    menu: document.getElementById("scene-menu"),
    game: document.getElementById("scene-game"),
    bloom: document.getElementById("scene-bloom"),
    settings: document.getElementById("scene-settings"),
    credits: document.getElementById("scene-credits"),
  };

  const sceneManager = new SceneManager(bus, {
    animation,
    getElement: (id) => sceneElements[id],
  });

  // ── Splash ────────────────────────────────────────────
  const splashCta = document.getElementById("splashCta") as HTMLButtonElement | null;
  if (!splashCta) throw new Error("Missing #splashCta");

  sceneManager.register(
    new SplashScene({
      element: sceneElements.splash!,
      cta: splashCta,
      onEnterRequested: () => sceneManager.transitionTo("menu"),
    }),
  );

  // ── Menu ──────────────────────────────────────────────
  const menuPlay = document.querySelector<HTMLButtonElement>('#scene-menu [data-action="play"]');
  const menuSettings = document.querySelector<HTMLButtonElement>(
    '#scene-menu [data-action="settings"]',
  );
  const menuCredits = document.querySelector<HTMLButtonElement>(
    '#scene-menu [data-action="credits"]',
  );
  if (!menuPlay || !menuSettings || !menuCredits) {
    throw new Error("Missing menu buttons");
  }

  sceneManager.register(
    new MenuScene({
      element: sceneElements.menu!,
      buttons: { play: menuPlay, settings: menuSettings, credits: menuCredits },
      onPlay: async () => {
        await sceneManager.transitionTo("game");
        void onboarding.maybeShow();
      },
      onSettings: () => sceneManager.transitionTo("settings"),
      onCredits: () => sceneManager.transitionTo("credits"),
    }),
  );

  // ── Game ──────────────────────────────────────────────
  const gameScene = new GameScene({ targets, renderer, state, bus, platform, animation });
  sceneManager.register(gameScene);

  // ── Bloom ─────────────────────────────────────────────
  const bloomReplay = document.getElementById("bloomReplay") as HTMLButtonElement | null;
  const bloomMenu = document.getElementById("bloomMenu") as HTMLButtonElement | null;
  if (!bloomReplay) throw new Error("Missing #bloomReplay");
  if (!bloomMenu) throw new Error("Missing #bloomMenu");

  sceneManager.register(
    new BloomScene({
      element: sceneElements.bloom!,
      replayButton: bloomReplay,
      menuButton: bloomMenu,
      onReplay: async () => {
        gameScene.reset();
        await sceneManager.transitionTo("game");
        void onboarding.maybeShow();
      },
      onMenu: () => sceneManager.transitionTo("menu"),
    }),
  );

  // ── Settings ──────────────────────────────────────────
  const settingsSoundToggle = document.getElementById("settingSound") as HTMLInputElement | null;
  const settingsHapticsToggle = document.getElementById(
    "settingHaptics",
  ) as HTMLInputElement | null;
  const settingsReset = document.getElementById("settingsReset") as HTMLButtonElement | null;
  const settingsBack = document.getElementById("settingsBack") as HTMLButtonElement | null;

  if (!settingsSoundToggle || !settingsHapticsToggle || !settingsReset || !settingsBack) {
    throw new Error("Missing settings elements");
  }

  sceneManager.register(
    new SettingsScene({
      element: sceneElements.settings!,
      soundToggle: settingsSoundToggle,
      hapticsToggle: settingsHapticsToggle,
      resetButton: settingsReset,
      backButton: settingsBack,
      storage: platform.storage,
      onBack: () => sceneManager.transitionTo("menu"),
    }),
  );

  // ── Credits ───────────────────────────────────────────
  const creditsBack = document.getElementById("creditsBack") as HTMLButtonElement | null;
  if (!creditsBack) throw new Error("Missing #creditsBack");

  sceneManager.register(
    new CreditsScene({
      element: sceneElements.credits!,
      backButton: creditsBack,
      onBack: () => sceneManager.transitionTo("menu"),
    }),
  );

  // ── Onboarding ────────────────────────────────────────
  const onboardingEl = document.getElementById("onboarding");
  const onboardingNext = document.getElementById("onboardingNext") as HTMLButtonElement | null;
  const onboardingSkip = document.getElementById("onboardingSkip") as HTMLButtonElement | null;
  if (!onboardingEl || !onboardingNext || !onboardingSkip) {
    throw new Error("Missing onboarding elements");
  }

  const onboardingSteps = Array.from(
    onboardingEl.querySelectorAll<HTMLElement>(".onboarding-step"),
  );
  const onboardingDots = Array.from(onboardingEl.querySelectorAll<HTMLElement>(".onboarding-dot"));

  const onboarding = new OnboardingOverlay({
    element: onboardingEl,
    steps: onboardingSteps,
    dots: onboardingDots,
    nextButton: onboardingNext,
    skipButton: onboardingSkip,
    storage: platform.storage,
  });

  // ── Pause overlay ─────────────────────────────────────
  const pauseBtn = document.getElementById("gamePauseBtn") as HTMLButtonElement | null;
  const pauseOverlay = document.getElementById("pauseOverlay");
  const pauseResume = document.getElementById("pauseResume") as HTMLButtonElement | null;
  const pauseMenu = document.getElementById("pauseMenu") as HTMLButtonElement | null;
  if (!pauseBtn || !pauseOverlay || !pauseResume || !pauseMenu) {
    throw new Error("Missing pause elements");
  }

  const openPause = (): void => {
    gameScene.pause();
    pauseOverlay.classList.add("is-visible");
    pauseOverlay.setAttribute("aria-hidden", "false");
  };
  const closePause = (): void => {
    pauseOverlay.classList.remove("is-visible");
    pauseOverlay.setAttribute("aria-hidden", "true");
    gameScene.resume();
  };

  pauseBtn.addEventListener("click", openPause);
  pauseResume.addEventListener("click", closePause);
  pauseMenu.addEventListener("click", () => {
    pauseOverlay.classList.remove("is-visible");
    pauseOverlay.setAttribute("aria-hidden", "true");
    void sceneManager.transitionTo("menu");
  });

  // Показываем pause-button только во время game-сцены
  const updatePauseVisibility = (sceneId: SceneId): void => {
    pauseBtn.classList.toggle("is-hidden", sceneId !== "game");
    if (sceneId !== "game") {
      pauseOverlay.classList.remove("is-visible");
      pauseOverlay.setAttribute("aria-hidden", "true");
    }
  };
  bus.on("scene:change", ({ to }) => {
    updatePauseVisibility(to);
  });
  updatePauseVisibility("splash");

  // ── Events ────────────────────────────────────────────
  bus.on("level:complete", () => {
    void sceneManager.transitionTo("bloom");
  });

  const loop = new GameLoop([(ctx) => sceneManager.update(ctx)]);
  loop.start();

  // Старт: показываем splash
  void sceneManager.transitionTo("splash");

  // PWA SW registration via workbox-window
  const updateSW = registerSW({
    onNeedRefresh() {
      showUpdateToast(() => void updateSW(true));
    },
    onOfflineReady() {
      console.warn("Boo Bloom is ready for offline play.");
    },
    immediate: true,
  });
};

bootstrap();
